import type { ElementType } from 'react'

import {
  Boxes,
  FileKey,
  FileSignature,
  FileText,
  HandCoins,
  Layers,
  Telescope,
} from 'lucide-react'

import type { MonitorRecipeTarget } from '@/types/Monitor'

export type EntityTargetKind = Extract<MonitorRecipeTarget, 'vendor' | 'agency'>

export type ResourceTargetKind = Extract<
  MonitorRecipeTarget,
  'opportunity' | 'contract' | 'idv' | 'ota' | 'otidv' | 'grant' | 'forecast'
>

export type WebTargetKind = Extract<MonitorRecipeTarget, 'web' | 'website'>

/**
 * Identifier labels keyed by the recipe-param field carrying the identifier.
 * Single source for the wizard, dashboard chips, and workspace identifier
 * tags — keep the three surfaces' vocabulary identical.
 */
export const MONITOR_IDENTIFIER_LABELS: Record<string, string> = {
  uei: 'UEI',
  agency: 'Agency',
  opportunity_id: 'Opportunity ID',
  contract_key: 'Contract ID',
  idv_key: 'IDV ID',
  ota_key: 'OTA ID',
  otidv_key: 'OT IDV ID',
  grant_id: 'Grant ID',
  forecast_id: 'Forecast ID',
}

export interface ResourceTargetOption {
  kind: ResourceTargetKind
  title: string
  helper: string
  identifierLabel: string
  placeholder: string
  Icon: ElementType<{ className?: string }>
}

export const RESOURCE_TARGET_OPTION_BY_KIND: Record<
  ResourceTargetKind,
  ResourceTargetOption
> = {
  opportunity: {
    kind: 'opportunity',
    title: 'Opportunity',
    helper: 'Track a solicitation or notice',
    identifierLabel: MONITOR_IDENTIFIER_LABELS.opportunity_id,
    placeholder: 'Title keyword or opportunity ID',
    Icon: FileText,
  },
  contract: {
    kind: 'contract',
    title: 'Contract',
    helper: 'Track a prime award',
    identifierLabel: MONITOR_IDENTIFIER_LABELS.contract_key,
    placeholder: 'Title keyword or contract ID',
    Icon: FileSignature,
  },
  idv: {
    kind: 'idv',
    title: 'IDV',
    helper: 'Track an IDIQ or contract vehicle',
    identifierLabel: MONITOR_IDENTIFIER_LABELS.idv_key,
    placeholder: 'Title keyword or IDV ID',
    Icon: Layers,
  },
  ota: {
    kind: 'ota',
    title: 'OTA',
    helper: 'Track an Other Transaction Authority award',
    identifierLabel: MONITOR_IDENTIFIER_LABELS.ota_key,
    placeholder: 'Title keyword or OTA ID',
    Icon: FileKey,
  },
  otidv: {
    kind: 'otidv',
    title: 'OT IDV',
    helper: 'Track an Other Transaction IDV (umbrella OTA)',
    identifierLabel: MONITOR_IDENTIFIER_LABELS.otidv_key,
    placeholder: 'Title keyword or OT IDV ID',
    Icon: Boxes,
  },
  grant: {
    kind: 'grant',
    title: 'Grant',
    helper: 'Track a grant opportunity',
    identifierLabel: MONITOR_IDENTIFIER_LABELS.grant_id,
    placeholder: 'Title keyword or grant ID',
    Icon: HandCoins,
  },
  forecast: {
    kind: 'forecast',
    title: 'Forecast',
    helper: 'Track a forecast',
    identifierLabel: MONITOR_IDENTIFIER_LABELS.forecast_id,
    placeholder: 'Title keyword or forecast ID',
    Icon: Telescope,
  },
}

/**
 * Resource kinds offered in the wizard's type picker. Opportunity is
 * intentionally excluded — opportunity coverage comes from entity/agency
 * monitors instead, not a standalone resource to track. (The by-kind map keeps
 * its entry so any pre-existing opportunity monitor still resolves a label/icon.)
 */
const OFFERED_RESOURCE_TARGET_KINDS: readonly ResourceTargetKind[] = [
  'contract',
  'idv',
  'ota',
  'otidv',
  'grant',
  'forecast',
]

export const RESOURCE_TARGET_OPTIONS: ResourceTargetOption[] =
  OFFERED_RESOURCE_TARGET_KINDS.map(
    (kind) => RESOURCE_TARGET_OPTION_BY_KIND[kind],
  )

export const isResourceTargetKind = (
  kind: MonitorRecipeTarget | null | undefined,
): kind is ResourceTargetKind =>
  kind === 'opportunity' ||
  kind === 'contract' ||
  kind === 'idv' ||
  kind === 'ota' ||
  kind === 'otidv' ||
  kind === 'grant' ||
  kind === 'forecast'

export const resourceOptionForKind = (
  kind: ResourceTargetKind,
): ResourceTargetOption => RESOURCE_TARGET_OPTION_BY_KIND[kind]

/**
 * A labeled cluster of example queries shown in a wizard picker's idle state so
 * an empty search box reads like a curated gallery instead of a blank field.
 * Shared by the entity (vendor/agency name) and resource (title-keyword)
 * pickers, which render groups identically.
 */
export interface MonitorExampleGroup {
  /** Domain heading shown above the group's example pills. */
  label: string
  queries: readonly string[]
}

/**
 * Entity-picker starter names, grouped by domain. Every query is a real,
 * distinctive name (never a bare abbreviation like "RTX"/"SAIC", which the
 * typeahead ranks poorly against substrings) so a click returns a clean live
 * result.
 */
export const ENTITY_TARGET_EXAMPLE_GROUPS: Record<
  EntityTargetKind,
  readonly MonitorExampleGroup[]
> = {
  vendor: [
    {
      label: 'Defense primes',
      queries: [
        'Lockheed Martin',
        'Northrop Grumman',
        'General Dynamics',
        'RTX Corporation',
      ],
    },
    {
      label: 'Defense tech',
      queries: ['Palantir', 'Anduril', 'Skydio', 'Saronic'],
    },
    {
      label: 'IT & services',
      queries: [
        'Leidos',
        'Booz Allen Hamilton',
        'Peraton',
        'CACI International',
      ],
    },
  ],
  agency: [
    {
      label: 'Defense',
      queries: [
        'Department of Defense',
        'Department of the Army',
        'Department of the Navy',
        'Department of the Air Force',
      ],
    },
    {
      label: 'Civilian',
      queries: [
        'General Services Administration',
        'Department of Veterans Affairs',
        'NASA',
        'Department of Health and Human Services',
      ],
    },
    {
      label: 'Security & science',
      queries: [
        'Department of Homeland Security',
        'Department of State',
        'Department of Energy',
        'National Science Foundation',
      ],
    },
  ],
}

/**
 * Resource-picker starter searches, grouped by work area. The resource step is
 * kind-agnostic (contracts, IDVs, OTAs, grants, forecasts all search by title),
 * so these are broad title keywords — not identifiers — that return matches
 * across kinds, mirroring the entity gallery's warm start.
 */
export const RESOURCE_EXAMPLE_GROUPS: readonly MonitorExampleGroup[] = [
  {
    label: 'Cyber & IT',
    queries: ['cybersecurity', 'cloud migration', 'software development'],
  },
  {
    label: 'Logistics & facilities',
    queries: ['logistics support', 'facilities management', 'supply chain'],
  },
  {
    label: 'Professional services',
    queries: ['program management', 'advisory services', 'training'],
  },
  {
    label: 'Research & engineering',
    queries: ['research and development', 'systems engineering', 'prototyping'],
  },
]
